'use client';

import useModalManager from '@hooks/useModalManager';
import usePersistentState from '@hooks/usePersistentState';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import { Button } from '@ui';

const sessionDismissed = new Set<string>();

export interface AnnouncementModalProps {
    id: string;
    endDate: Date | string;
    title?: ReactNode;
    children: ReactNode;
    size?: number | string;
    closeLabel?: string;
    dismissForeverLabel?: string;
    onDismiss?: (permanent: boolean) => void;
}

const AnnouncementModal = ({
    id,
    endDate,
    title,
    children,
    size,
    closeLabel = 'Schließen',
    dismissForeverLabel = 'Dauerhaft nicht mehr anzeigen',
    onDismiss
}: AnnouncementModalProps) => {
    const { addModal, removeModal } = useModalManager();
    const { events } = useRouter();
    const [permanentDismissed, setPermanentDismissed] = usePersistentState<boolean>(
        `announcement-dismissed__${id}`,
        false
    );

    useEffect(() => {
        const open = () => {
            if (permanentDismissed || sessionDismissed.has(id)) return;

            const end = new Date(endDate);
            if (isNaN(end.getTime()) || new Date() >= end) return;

            const alreadyOpen = useModalManager
                .getState()
                .modals.some((m) => (m as { announcementId?: string }).announcementId === id);
            if (alreadyOpen) return;

            const dismissSession = (modalId: string) => {
                sessionDismissed.add(id);
                removeModal(modalId);
                onDismiss?.(false);
            };

            const dismissForever = (modalId: string) => {
                setPermanentDismissed(true);
                sessionDismissed.add(id);
                removeModal(modalId);
                onDismiss?.(true);
            };

            addModal({
                announcementId: id,
                title,
                size,
                content: (modalId: string): ReactNode => (
                    <div>
                        {children}
                        <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
                            <Button color="light" flat onPress={() => dismissSession(modalId)}>
                                {closeLabel}
                            </Button>
                            <Button color="secondary" onPress={() => dismissForever(modalId)}>
                                {dismissForeverLabel}
                            </Button>
                        </div>
                    </div>
                )
            } as Parameters<typeof addModal>[0]);
        };

        const schedule = () => setTimeout(open, 0);

        schedule();
        events.on('routeChangeComplete', schedule);
        return () => events.off('routeChangeComplete', schedule);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export default AnnouncementModal;
